// What /welcome shows once the account is in a company but the console isn't open yet:
// - "Finishing setup…": a new company's defaults are still on their way to the server (retrying, or waiting for Try
//   again). A company without settings would send iPhones to setup, so the console doesn't open before they land.
// - "You joined …" / "… is ready": memberships are being re-read; the route guard then opens the console. If that
//   check can't reach the server, the reason and Try again.

import { Building, CircleCheck, RefreshCw } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Button, Card, GT_COLORS, IconBadge, Spinner } from '@/ui';
import { OPENING_TEXT } from '../welcomeText';
import { ErrorNote } from './Bits';

export type ProgressPanelProps =
  | {
      kind: 'finishing';
      title: string;
      detail: string;
      /** What went wrong on the last try; null on the first one. */
      problem: string | null;
      /** Another try is coming by itself; false = it's the person's turn (Try again). */
      retrying: boolean;
      onRetry: () => void;
    }
  | {
      kind: 'opening';
      title: string;
      /** The console is being opened. */
      opening: boolean;
      /** Shown when opening didn't happen (the membership check failed). */
      problem: string | null;
      onRetry: () => void;
    };

export function ProgressPanel(props: ProgressPanelProps) {
  const working = props.kind === 'finishing' ? props.retrying : props.opening;
  const heading = useRef<HTMLHeadingElement | null>(null);

  // The panel replaces the section whose button was pressed (or the dialog that asked): keyboard and screen-reader
  // focus would fall back to the top of the page, so it moves to the panel's title instead.
  useEffect(() => {
    const active = document.activeElement;
    if (active === null || active === document.body || !active.isConnected) heading.current?.focus();
  }, [props.kind]);

  return (
    <Card as="section" padding="lg" aria-labelledby="welcome-progress-title" className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <IconBadge
          icon={props.kind === 'opening' ? <CircleCheck /> : <Building />}
          color={props.kind === 'opening' ? GT_COLORS.green : GT_COLORS.teal}
          size={44}
        />
        <div className="min-w-0">
          <h2 ref={heading} id="welcome-progress-title" tabIndex={-1} className="text-lg font-bold text-fg outline-none">
            {props.title}
          </h2>
          {props.kind === 'finishing' && <p className="mt-0.5 text-sm text-fg-muted">{props.detail}</p>}
        </div>
      </div>
      <div aria-live="polite" className="flex flex-col gap-3">
        {props.kind === 'opening' && props.opening && (
          <p className="flex items-center gap-2 text-sm font-medium text-fg-muted">
            <Spinner size="sm" label={null} className="text-primary" />
            {OPENING_TEXT}
          </p>
        )}
        {props.kind === 'finishing' && props.retrying && (
          <p className="flex items-center gap-2 text-sm font-medium text-fg-muted">
            <Spinner size="sm" label={null} className="text-primary" />
            {props.problem ?? 'Saving…'}
          </p>
        )}
        {!working && props.problem && <ErrorNote>{props.problem}</ErrorNote>}
      </div>
      {!working && (
        <Button variant="primary" size="lg" fullWidth icon={<RefreshCw />} onClick={props.onRetry}>
          Try again
        </Button>
      )}
    </Card>
  );
}
